import { Component, ViewEncapsulation, ViewChild, ElementRef, Output, 
  EventEmitter, Inject, AfterViewInit, Input } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, of, zip } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';

import { environment as ENV } from '@environment';
import { TranslocoService } from '@jsverse/transloco';
import { MobileSectorVm, FlightAmenitie, NewFlightItemVm } from '@app/entities/flight-results';
import { FlightItineraryAbstractComponent } from '@shared/flight-itinerary/abstracts/flight-itinerary.abstract';
import { FlightClass } from '@shared/base-search-bar/entities/flight-class';
import { AppFacade } from '@app/app.facade';
import { TIME_TO_SLIDE, ANIMATIONS } from '@app/constants/animations.constants';
import { IS_BROWSER } from '@app/modules/core/tokens/app.tokens';
import { TripTypes } from '@app/entities/trip-types';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';
import { FlightResultsCabinFacade } from '@flight-results/flight-results-cabin.facade';
import { cabinTypeNameMapper } from '@flight-results/mappers/flights.mapper';
import { flightAmplitudeProductPropertiesFromResultsMapper } from '@app/modules/core/mappers/flights-amplitude.mapper';
import { PackageStep } from '@packages/packages-search-bar/constants/package-step';
import { FlightCabin, FlightCabinPayloadParam, FlightDetailCabinPayload } from '@shared/flight-itinerary/entities/cabin-types';
import { MobileDeviceService } from '@app/services/mobile-device.service';
import { PropertiesAmplitudeService } from '@app/services/properties-amplitude.service';
import { BaseSearchBarFacade } from '@shared/base-search-bar/base-search-bar-facade';
import { SOURCE_PARAM } from '@app/modules/core/constants/query-params';
import { AvoService } from '@avo/services/avo.service';
import { AvoDispatchOrchestrator } from '@avo/orchestrator/avo.dispatch';
import { AVO_EVENTS } from '@avo/events/avo.events';
import { HeaderWidgetConfig, HeaderWidgetType } from '@design-system/components/templates/header/entities/header.types';
import { BackgroundSurfaceType } from '@design-system/components/atoms/background-surface/entities/background-surface.types';

@Component({
  selector: 'app-mobile-itinerary-detail.component',
  templateUrl: './mobile-itinerary-detail.component.html',
  styleUrls: ['./mobile-itinerary-detail.component.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  standalone: false
})
export class MobileItinerayDetailComponent extends FlightItineraryAbstractComponent implements AfterViewInit {

  @ViewChild('container') public container: ElementRef;
  @ViewChild('bodycontainer') public bodyContainer: ElementRef;

  @Input() isLoading = false;
  @Output() selectFlightsEvent: EventEmitter<{ flights: NewFlightItemVm[], itineraryPrice: string }> =
    new EventEmitter<{ flights: NewFlightItemVm[], itineraryPrice: string }>();
  @Output() selectFlightsPackageEvent: EventEmitter<{ flights: NewFlightItemVm[], optionId: number }> =
    new EventEmitter<{ flights: NewFlightItemVm[], optionId: number }>();

  public options: NewFlightItemVm[];
  public flightClass: FlightClass;
  public provider: string;
  public animation: string = ANIMATIONS.SLIDE_IN_RIGHT;
  public price: string;
  public sectorNumberSelected: number;
  public isPackageView: boolean;
  public isInternational: boolean;

  public readonly backgroudSurface = BackgroundSurfaceType.Primary;

  protected AvoDispatch = new AvoDispatchOrchestrator();

  private optionId: number;
  private amplitudeProvider: SearchBy = SearchBy.FLIGHTS;

  constructor(
    private facade: AppFacade,
    private flightsCabinFacade: FlightResultsCabinFacade,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private mobileDeviceService: MobileDeviceService,
    private baseSearchBarFacade: BaseSearchBarFacade,
    private amplitude: PropertiesAmplitudeService,
    private readonly avoService: AvoService,
    private readonly transloco: TranslocoService,
    @Inject(IS_BROWSER) public isBrowser: boolean) {
    super();
    if (this.router.getCurrentNavigation()?.extras && this.router.getCurrentNavigation()?.extras?.state) {
      this.options = this.router.getCurrentNavigation().extras.state.options;
      this.flightClass = this.router.getCurrentNavigation().extras.state.flightClass;
      this.provider = this.router.getCurrentNavigation().extras.state.provider;
      this.price = this.router.getCurrentNavigation().extras.state.price;
      this.sectorNumberSelected = this.router.getCurrentNavigation().extras.state.sectorNumberSelected;
      this.optionId = this.router.getCurrentNavigation().extras.state.optionId;
      this.isPackageView = !!this.router.getCurrentNavigation().extras.state.isPackageView;
      this.isInternational = !!this.router.getCurrentNavigation().extras.state.isInternational;
      if (this.isPackageView) this.amplitudeProvider = SearchBy.PACKAGES;
    }
  }

  private mapOptions(options: NewFlightItemVm[]): FlightCabinPayloadParam[] {
    const values = options
      .map((o, optionIdx) => o.Flights.map((f, idx) => {
        const index = this.getCabinIndex(o.SectorNumber, 0, idx);
        return {
          AirlineCode: f.OpAirlineCode,
          Equipment: f.Equipment,
          FlightNumber: Number(f.Number),
          DepartureAirport: f.DepartureAirport,
          ArrivalAirport: f.ArrivalAirport,
          DepartureDateTime: f.DepartureDate,
          CabinType: this.cabinTypeName(f.Cabin),
          Index: index
        } as FlightCabinPayloadParam;
      }))
      .reduce((arr, f) => [...arr, ...f]);
    return values;
  }

  public getCabinIndex(sectorNumber: number, index: number, sectorIndex: number): number {
    return (sectorNumber * 100) + (sectorIndex * 10) + index;
  }

  ngAfterViewInit(): void {
    if (this.isBrowser) {
      const positionSectorSelected = this.options.findIndex(route => route.SectorNumber === this.sectorNumberSelected);
      if (positionSectorSelected > -1) {
        this.scrollToPosition(this.bodyContainer.nativeElement.children[positionSectorSelected].offsetTop);
      }
      this.flightsCabinFacade.clearFlightResultsCabin();
      this.flightsCabinFacade.fetchFlightResultsCabin({
        ItineraryId: 0,
        Flights: this.mapOptions(this.options),
        Language: ENV.language
      });
      zip(
        this.activatedRoute.queryParamMap.pipe(take(1)),
        this.amplitude.searchProperties$(this.amplitudeProvider)
      ).pipe(
        map(([queries, searchProps]) => ({
          eventName: AVO_EVENTS.VIEW_TRAVEL_ITEM_DETAIL,
          evenParams: { SOURCE: queries.get(SOURCE_PARAM) },
          searchProps, productProps: flightAmplitudeProductPropertiesFromResultsMapper(
            this.options, this.provider, null, this.options?.[0]?.HasPrivateFare
          )
        })),
        switchMap(({ eventName, evenParams, searchProps, productProps }) =>
          this.avoService.getEventTriggerProps$(eventName, evenParams, searchProps, productProps).pipe(
            tap(trigger => {
              this.AvoDispatch.viewTravelItemDetail({
                ...this.avoService.avoBaseProperties(trigger),
                ...this.avoService.avoSearchProps(trigger?.SEARCH_PROPS),
                ...this.avoService.avoProductProps(trigger?.PRODUCT_PROPS),
                source: this.avoService.getAvoSourceValueType(trigger?.EVENT_PROPS?.SOURCE),
                index: trigger?.EVENT_PROPS?.INDEX as number
              });
            })
          ))
      ).subscribe().unsubscribe();
    }
  }

  get isLoadingFlightCabinDetail$(): Observable<boolean> {
    return this.flightsCabinFacade.isLoadingFlightCabinDetail$;
  }

  get cabinAmenities$(): Observable<FlightAmenitie[]> {
    return this.isPackageView ? of([]) : this.flightsCabinFacade.flightCabinsDetailSelected$.pipe(
      map(arr => {
        if (!arr) return [];
        const selected = arr.find(b => b.itineraryId === 0);
        if (selected) return selected.amenities;
        return [];
      })
    );
  }

  get isLoggedIn$(): Observable<boolean> {
    return this.facade.isLoggedIn$;
  }

  get isLoadingFlightCabin$(): Observable<boolean> {
    return this.flightsCabinFacade.isLoadingFlightCabin$;
  }

  get flightCabinSelected$(): Observable<FlightCabin> {
    return this.flightsCabinFacade.flightCabinSelected$;
  }

  get isPackageModuleActive$(): Observable<boolean> {
    return this.baseSearchBarFacade.packageStep$.pipe(map(packageStep => packageStep === PackageStep.FLIGHT_RESULTS));
  }

  get isMobile$(): Observable<boolean> {
    return this.mobileDeviceService.isMobileDevice$;
  }

  get headerWidgets$(): Observable<HeaderWidgetConfig[]> {
    return this.transloco.selectTranslate('FLIGHT_RESULTS_MODULE.ITINERARY_LIST_SECTION.FLIGHTS').pipe(
      map(label => [
        {
          Type: HeaderWidgetType.Title,
          Label: label,
          LabelAlign: 'center'
        },
        {
          Type: HeaderWidgetType.Close,
        },
      ])
    );
  }

  private cabinTypeName(cabinType: string): string {
    return cabinTypeNameMapper(cabinType);
  }

  public goBack(): void {
    if (!this.isLoading) {
      this.animation = ANIMATIONS.SLIDE_OUT_RIGHT;
      setTimeout(() => {
        this.facade.navigateToLastRoute();
      }, TIME_TO_SLIDE);
    }
  }

  public tripTypeHeader(route: MobileSectorVm): string {
    return route.SectorNumber === TripTypes.DEPARTURE ?
      'FLIGHT_RESULTS_MODULE.ITINERARY_LIST_SECTION.DEPARTURE_TRIP' :
      'FLIGHT_RESULTS_MODULE.ITINERARY_LIST_SECTION.RETURN_TRIP';
  }

  public isReturnHeader(route: MobileSectorVm): boolean {
    return route.SectorNumber !== TripTypes.DEPARTURE;
  }

  public selectFlights(): void {
    this.isPackageModuleActive$.subscribe(isPackageModuleActive => {
      if (isPackageModuleActive) {
        this.selectFlightsPackageEvent.emit({
          flights: this.options,
          optionId: this.optionId
        });
        this.goBack();
      } else this.selectFlightsEvent.emit({ flights: this.options, itineraryPrice: this.price });
    }).unsubscribe();
  }

  public scrollToPosition(top: number): void {
    this.bodyContainer.nativeElement.scrollTo({
      left: 0, top: top
    });
  }

  public openCabinDetail(payload: FlightDetailCabinPayload): void {
    this.flightsCabinFacade.selectFlightCabin(payload);
  }
}
